import { z } from 'zod';

/**
 * Equipment Schemas
 *
 * Defines PV (solar), ElectricStorage (battery), and Generator configurations.
 * Each supports 'prescribed' (fixed size) and 'optimal' (solver decides) modes.
 */

/**
 * PV (Solar) Configuration Schema
 */
export const PVSchema = z.object({
  // Capacity limits
  max_kw: z.number()
    .min(0, 'PV max capacity cannot be negative'),

  min_kw: z.number()
    .min(0, 'PV min capacity cannot be negative')
    .default(0),

  // Cost parameters
  installed_cost_per_kw: z.number()
    .min(0, 'PV cost cannot be negative')
    .default(1800),

  // Incentives
  federal_itc_fraction: z.number()
    .min(0, 'ITC fraction must be >= 0')
    .max(1, 'ITC fraction must be <= 1')
    .default(0.30),

  // Performance parameters (optional)
  degradation_fraction: z.number().min(0).max(0.05).optional(),
  tilt: z.number().min(0).max(90).optional(),
  azimuth: z.number().min(0).max(360).optional(),
  losses: z.number().min(0).max(0.5).optional(),
  dc_ac_ratio: z.number().min(0.5).max(2.0).optional(),

  // Existing system
  existing_kw: z.number().min(0).optional(),
}).refine(
  (pv) => pv.min_kw <= pv.max_kw,
  { message: 'PV min_kw cannot exceed max_kw' }
);

/**
 * Electric Storage (Battery) Configuration Schema
 */
export const ElectricStorageSchema = z.object({
  // Power capacity (kW)
  max_kw: z.number()
    .min(0, 'Battery max power cannot be negative'),

  min_kw: z.number()
    .min(0, 'Battery min power cannot be negative')
    .default(0),

  // Energy capacity (kWh)
  max_kwh: z.number()
    .min(0, 'Battery max energy cannot be negative'),

  min_kwh: z.number()
    .min(0, 'Battery min energy cannot be negative')
    .default(0),

  // Cost parameters
  installed_cost_per_kw: z.number()
    .min(0, 'Battery power cost cannot be negative')
    .default(800),

  installed_cost_per_kwh: z.number()
    .min(0, 'Battery energy cost cannot be negative')
    .default(400),

  // Operating parameters
  soc_min_fraction: z.number()
    .min(0, 'Min SOC must be >= 0')
    .max(1, 'Min SOC must be <= 1')
    .default(0.2),

  can_grid_charge: z.boolean().default(true),

  // Degradation & cycling
  cycle_cost_per_kwh: z.number().min(0).default(0.05),

  // Incentives
  federal_itc_fraction: z.number().min(0).max(1).optional(),
}).refine(
  (batt) => batt.min_kw <= batt.max_kw,
  { message: 'Battery min_kw cannot exceed max_kw' }
).refine(
  (batt) => batt.min_kwh <= batt.max_kwh,
  { message: 'Battery min_kwh cannot exceed max_kwh' }
);

/**
 * Generator (Diesel) Configuration Schema
 */
export const GeneratorSchema = z.object({
  // Capacity
  max_kw: z.number()
    .min(0, 'Generator max capacity cannot be negative'),

  min_kw: z.number()
    .min(0, 'Generator min capacity cannot be negative')
    .default(0),

  // Fuel parameters
  fuel_cost_per_gallon: z.number()
    .min(0, 'Fuel cost cannot be negative')
    .default(3.0),

  fuel_avail_gal: z.number().min(0).optional(),

  // Efficiency
  electric_efficiency_full_load: z.number()
    .min(0.1)
    .max(0.6)
    .optional(),

  // Cost parameters
  installed_cost_per_kw: z.number().min(0).optional(),
  om_cost_per_kw: z.number().min(0).optional(),

  // Existing generator
  existing_kw: z.number().min(0).optional(),
}).refine(
  (gen) => gen.min_kw <= gen.max_kw,
  { message: 'Generator min_kw cannot exceed max_kw' }
);

/**
 * CHP (Natural Gas Generator) Configuration Schema
 */
export const CHPSchema = z.object({
  max_kw: z.number()
    .min(0, 'CHP max capacity cannot be negative'),

  min_kw: z.number()
    .min(0, 'CHP min capacity cannot be negative')
    .default(0),

  fuel_cost_per_mmbtu: z.number()
    .min(0, 'Fuel cost cannot be negative')
    .default(6.0),

  electric_efficiency_full_load: z.number().min(0.1).max(0.6).optional(),

  installed_cost_per_kw: z.number().min(0).optional(),
}).refine(
  (chp) => chp.min_kw <= chp.max_kw,
  { message: 'CHP min_kw cannot exceed max_kw' }
);

/**
 * Determine sizing mode from min/max values
 */
export const getSizingMode = (config) => {
  if (!config) return null;
  if (config.min_kw > 0 && config.min_kw === config.max_kw) {
    return 'prescribed';
  }
  return 'optimal';
};

/**
 * Set sizing mode by adjusting min_kw
 */
export const setSizingMode = (config, mode) => {
  if (mode === 'prescribed') {
    return { ...config, min_kw: config.max_kw };
  }
  return { ...config, min_kw: 0 };
};

export default {
  PVSchema,
  ElectricStorageSchema,
  GeneratorSchema,
  CHPSchema,
};
